import React, { Suspense, lazy } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useQuery } from 'react-query';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingSpinner from './components/ui/LoadingSpinner';
import Layout from './components/layout/Layout';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ProgramsPage from './pages/ProgramsPage';
import ProgramDetailPage from './pages/ProgramDetailPage';
import RegistrationPage from './pages/RegistrationPage';
import ContactPage from './pages/ContactPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsConditionsPage from './pages/TermsConditionsPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import MyRegistrations from './pages/parent/MyRegistrations';
import MySubscriptions from './pages/parent/MySubscriptions';
import MyAttendance from './pages/parent/MyAttendance';
import NotFoundPage from './pages/NotFoundPage';
import MaintenancePage from './pages/MaintenancePage';
import { cmsAPI } from './services/api';

// Lazy load heavy components for better performance
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const CheckoutSuccessPage = lazy(() => import('./pages/CheckoutSuccessPage'));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));

// Suspense fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <LoadingSpinner size="lg" text="Loading..." />
  </div>
);

function App() {
  const location = useLocation();
  
  // Use the same query key as ManageSiteSettings so admin saves invalidate this too.
  const { data: settings } = useQuery('siteSettings', async () => {
    const res = await cmsAPI.getSettings();
    return res.data;
  }, {
    retry: false,
  });
  
  // Check if user is admin (from localStorage)
  const userStr = localStorage.getItem('user');
  const isAdmin = userStr ? JSON.parse(userStr)?.is_staff === true : false;
  
  // Show maintenance page if enabled (except for admin users and admin routes)
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isLoginRoute = location.pathname === '/login';
  
  if (settings?.maintenance_mode && !isAdmin && !isAdminRoute && !isLoginRoute) {
    return (
      <MaintenancePage 
        message={settings.maintenance_message}
        endTime={settings.maintenance_end_time}
      />
    );
  }
  
  return (
    <ErrorBoundary>
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            iconTheme: {
              primary: '#10B981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#EF4444',
              secondary: '#fff',
            },
          },
        }}
      />
      <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Layout />}>
        <Route index element={<HomePage />} />
        <Route path="about" element={<AboutPage />} />
        <Route path="programs" element={<ProgramsPage />} />
        <Route path="programs/:slug" element={<ProgramDetailPage />} />
        <Route path="registration" element={<RegistrationPage />} />
        <Route path="contact" element={<ContactPage />} />
        <Route path="terms" element={<TermsConditionsPage />} />
        <Route path="privacy" element={<PrivacyPolicyPage />} />
      </Route>

      {/* Auth Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Cart Route - Lazy loaded */}
      <Route path="/cart" element={<Layout />}>
        <Route
          index
          element={
            <Suspense fallback={<PageLoader />}>
              <CartPage />
            </Suspense>
          }
        />
      </Route>

      {/* Checkout Success Route */}
      <Route path="/checkout/success" element={<Layout />}>
        <Route
          index
          element={
            <Suspense fallback={<PageLoader />}>
              <CheckoutSuccessPage />
            </Suspense>
          }
        />
      </Route>

      {/* Checkout Route - Lazy loaded */}
      <Route 
        path="/checkout" 
        element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <CheckoutPage />
            </Suspense>
          </ProtectedRoute>
        } 
      />

      {/* Protected User Dashboard - Lazy loaded */}
      <Route
        path="/dashboard/*"
        element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <DashboardPage />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* My Registrations - Parent Portal */}
      <Route path="/my-registrations" element={<Layout />}>
        <Route
          index
          element={
            <ProtectedRoute>
              <MyRegistrations />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* My Subscriptions - Parent Portal */}
      <Route path="/my-subscriptions" element={<Layout />}>
        <Route
          index
          element={
            <ProtectedRoute>
              <MySubscriptions />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* My Attendance - Parent Portal */}
      <Route path="/my-attendance" element={<Layout />}>
        <Route
          index
          element={
            <ProtectedRoute>
              <MyAttendance />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Admin Dashboard - Lazy loaded */}
      <Route
        path="/admin/*"
        element={
          <AdminRoute>
            <Suspense fallback={<PageLoader />}>
              <AdminDashboard />
            </Suspense>
          </AdminRoute>
        }
      />

      {/* 404 Not Found */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </ErrorBoundary>
  );
}

export default App;
